"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
Object.defineProperty(exports, "ResponsiveProp", {
  enumerable: true,
  get: function () {
    return _system.ResponsiveProp;
  }
});
exports.generateResponsiveStyles = generateResponsiveStyles;
exports.generateUnoptimizedResponsiveStyles = generateUnoptimizedResponsiveStyles;
exports.getResponsiveClassName = getResponsiveClassName;
exports.getResponsiveClassNamesForProp = getResponsiveClassNamesForProp;
var _system = require("../system");
var _ = require("./");
const ORIGIN_BREAKPOINT = 'xs';
function isBreakpointObject(sizes) {
  // See https://medium.com/@derek_develops/using-object-prototype-tostring-call-to-check-for-a-number-in-javascript-5dfb33edb6c5
  return Object.prototype.toString.call(sizes) === '[object Object]';
}
function isOrigin(breakpoint) {
  return breakpoint === ORIGIN_BREAKPOINT;
}

/**
 * Returns a generated classname string built with the 3 arguments:
 * ResponsiveClassName = classPrefix (+ breakpoint) + variantName
 */
function getResponsiveClassName(classPrefix, variantName) {
  let breakpoint = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ORIGIN_BREAKPOINT;
  return [classPrefix, isOrigin(breakpoint) ? '' : (0, _.pascalCase)(breakpoint), (0, _.pascalCase)(variantName.toString())].join('');
}

/**
 * Returns an array of responsive classnames that match the given responsive prop
 * ie: for responsiveProp = {xs: 1, md: 2} && classPrefix = 'size' =>
 * - size1 (Xs breakpoint is ignored)
 * - sizeMd2
 */
function getResponsiveClassNamesForProp(responsiveProp, classPrefix) {
  if (isBreakpointObject(responsiveProp)) {
    return Object.keys(responsiveProp).map(breakpoint => {
      const variantName = responsiveProp[breakpoint];
      if (variantName === undefined) return '';
      return getResponsiveClassName(classPrefix, variantName.toString(), breakpoint);
    });
  }
  return [getResponsiveClassName(classPrefix, responsiveProp.toString())];
}

/**
 * Returns a hasmap of responsive classname => css definition for all theme's breakpoint
 * ie For:
 *  - cssDefinitions = { color : { primary: { color: red }, secondary: { color: blue } } }
 *  - breakpoints = ['xs', 'sm']
 *
 * This will output the following css
 * {
 *   colorPrimary : { color: red },
 *   colorSecondary : { color: blue },
 *   '@media (min-width:600px)' : {
 *     colorXsPrimary : { color: red },
 *     colorSmSecondary : { color: blue },
 *   }
 * }
 **/
function generateResponsiveStyles(theme, cssDefinitions) {
  const breakpoints = theme.busbud.breakpoints;
  return breakpoints.keys.reduce((globalStyles, breakpoint) => {
    const breakpointKey = breakpoints.up(breakpoint);
    const styles = Object.keys(cssDefinitions).reduce((style, classPrefix) => {
      const variants = cssDefinitions[classPrefix];
      const variantNames = Object.keys(variants);
      variantNames.forEach(variantName => {
        const key = getResponsiveClassName(classPrefix, variantName, breakpoint);
        if (variants[variantName]) {
          style[key] = variants[variantName];
        }
      });
      return style;
    }, {});
    if (!isOrigin(breakpoint)) {
      globalStyles[breakpointKey] = styles;
    } else {
      Object.assign(globalStyles, styles);
    }
    return globalStyles;
  }, {});
}

/**
 * Returns a hasmap of responsive classname => css definition for all theme's breakpoint
 * ie For:
 *  - cssDefinitions = { color : { primary: { color: red }, secondary: { color: blue } } }
 *  - breakpoints = ['xs', 'sm']
 *
 * This will output the following css
 * {
 *   colorPrimary : { color: red },
 *   colorSecondary : { color: blue },
 *   '@media (min-width:600px)' : {
 *     colorXsPrimary : { color: red },
 *     colorSmSecondary : { color: blue },
 *   }
 * }
 **/
function generateUnoptimizedResponsiveStyles(theme, cssDefinitions) {
  const breakpoints = theme.busbud.breakpoints;
  return breakpoints.keys.reduce((globalStyles, breakpoint) => {
    const styles = {};
    Object.keys(cssDefinitions).forEach(classPrefix => {
      const variants = cssDefinitions[classPrefix];
      const variantNames = Object.keys(variants);
      variantNames.forEach(variantName => {
        const key = getResponsiveClassName(classPrefix, variantName, breakpoint);
        styles[key] = variants[variantName];
      });

      // No need for a media query for the first size.
      if (isOrigin(breakpoint)) {
        Object.assign(globalStyles, styles);
      } else {
        Object.keys(styles).forEach(styleKey => {
          globalStyles[styleKey] = globalStyles[styleKey] || {};
          globalStyles[styleKey][breakpoints.up(breakpoint)] = styles[styleKey];
        });
      }
    });
    return globalStyles;
  }, {});
}